import { useMemo, useState } from 'react'
import { Folder, FolderPlus, Layers, Paperclip } from 'lucide-react'
import { AssetPicker, type Asset } from 'tailwind-v4/blocks/asset-picker'
import { Input } from 'tailwind-v4/components/input'
import { cn } from 'tailwind-v4/lib/utils'
import inboxApi from '@/state/inbox/inbox.slice'
import type { InboxAssetType } from '@/state/entities/inbox/inbox.types'
import { useAssetUpload } from './use-asset-upload'

const ALL = '__all__'
const UNFILED = '__unfiled__'

const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

function toAsset(asset: InboxAssetType): Asset {
  return {
    id: asset.id,
    url: asset.url,
    filename: asset.original_filename,
    mimeType: asset.mime_type,
    sizeBytes: asset.size_bytes ?? undefined,
    createdAt: asset.created_at ?? undefined,
  }
}

/** Shorten a long filename for display while keeping the extension; the href
 * and `data-filename` keep the full original. */
function shortenName(filename: string): string {
  const dot = filename.lastIndexOf('.')
  const ext = dot > 0 ? filename.slice(dot) : ''
  const base = dot > 0 ? filename.slice(0, dot) : filename
  if (base.length <= 22) return filename
  return `${base.slice(0, 14)}…${base.slice(-4)}${ext}`
}

function assetToHtml(asset: Asset): string {
  // Insert a plain clickable link so the asset stays as text in the message and
  // works in the sent email. The visible text is auto-shortened; `data-asset` +
  // metadata let the badge row above the editor list it for preview. The href
  // stays the full URL regardless of the display text.
  return `<a data-asset data-mime="${asset.mimeType}" data-filename="${asset.filename}" href="${asset.url}" rel="noopener noreferrer" target="_blank">${shortenName(asset.filename)}</a>`
}

interface PassedProps {
  orgId: string
  serial?: string
  threadId?: string
  /** Receives an HTML snippet (image or link) to insert at the cursor. */
  onInsert: (html: string) => void
  /**
   * Extra classes for the trigger. The composer passes row classes when this
   * sits inside its mobile `⋯` popover instead of the editor toolbar.
   */
  className?: string
}

/**
 * Toolbar button that opens the v4 AssetPicker (Finder-style folder rail +
 * upload + library grid + pick), wired to the inbox asset API.
 */
export function AssetInsertButton({
  orgId,
  serial,
  threadId,
  onInsert,
  className,
}: PassedProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string>(ALL)
  const [draftFolders, setDraftFolders] = useState<string[]>([])
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const { data } = inboxApi.useGetAssetsQuery({ orgId })
  const { uploadAsset } = useAssetUpload(orgId)

  const source = useMemo(
    () =>
      (data?.data ?? []).filter(
        (asset) => asset.serial === null || asset.serial === serial
      ),
    [data, serial]
  )

  const folders = useMemo(() => {
    const set = new Set<string>(draftFolders)
    for (const a of source) if (a.folder) set.add(a.folder)
    return Array.from(set).sort((x, y) => x.localeCompare(y))
  }, [source, draftFolders])

  const counts = useMemo(() => {
    const map = new Map<string, number>()
    let unfiled = 0
    for (const a of source) {
      if (a.folder) map.set(a.folder, (map.get(a.folder) ?? 0) + 1)
      else unfiled += 1
    }
    return { map, unfiled, total: source.length }
  }, [source])

  const uploadFolder =
    selected === ALL || selected === UNFILED ? null : selected

  const visible = useMemo<Asset[]>(() => {
    const term = search.trim().toLowerCase()
    return source
      .filter((a) => {
        if (selected === UNFILED && a.folder) return false
        if (selected !== ALL && selected !== UNFILED && a.folder !== selected)
          return false
        if (term && !a.original_filename.toLowerCase().includes(term))
          return false
        return true
      })
      .map(toAsset)
  }, [source, selected, search])

  const handleUpload = async (files: File[]): Promise<Asset[]> => {
    const uploaded: Asset[] = []
    for (const file of files) {
      const asset = await uploadAsset(file, {
        serial,
        threadId,
        folder: uploadFolder,
      })
      if (asset) uploaded.push(toAsset(asset))
    }
    return uploaded
  }

  const commitNewFolder = () => {
    const name = newName.trim()
    if (name) {
      setDraftFolders((prev) => (prev.includes(name) ? prev : [...prev, name]))
      setSelected(name)
    }
    setNewName('')
    setCreating(false)
  }

  const railItem = (
    id: string,
    label: string,
    count: number,
    icon: React.ReactNode
  ) => (
    <button
      key={id}
      type="button"
      onClick={() => {
        setSelected(id)
      }}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
        selected === id
          ? 'bg-black/[0.06] font-medium text-foreground dark:bg-white/10'
          : 'text-muted-foreground hover:bg-black/[0.04] hover:text-foreground dark:hover:bg-white/5'
      )}
    >
      <span className="shrink-0 opacity-70">{icon}</span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className="shrink-0 text-xs tabular-nums opacity-60">{count}</span>
    </button>
  )

  const sidebar = (
    <div className="space-y-0.5 p-2">
      {railItem(ALL, 'All files', counts.total, <Layers className="size-4" />)}
      {folders.map((f) =>
        railItem(f, f, counts.map.get(f) ?? 0, <Folder className="size-4" />)
      )}
      {counts.unfiled > 0
        ? railItem(
            UNFILED,
            'Uncategorised',
            counts.unfiled,
            <Folder className="size-4" />
          )
        : null}

      {creating ? (
        <div className="px-1 pt-1">
          <Input
            autoFocus
            className="h-8"
            placeholder="Folder name…"
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commitNewFolder()
              } else if (e.key === 'Escape') {
                setCreating(false)
                setNewName('')
              }
            }}
            onBlur={commitNewFolder}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            setCreating(true)
          }}
          className="mt-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-black/[0.04] hover:text-foreground dark:hover:bg-white/5"
        >
          <FolderPlus className="size-4 shrink-0 opacity-70" />
          New folder
        </button>
      )}
    </div>
  )

  return (
    <>
      <button
        type="button"
        title="Insert asset"
        onClick={() => {
          setOpen(true)
        }}
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-colors',
          // Neutral toolbar action — see the note on MergeTagInsertButton.
          'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
          className
        )}
      >
        <Paperclip className="size-3" />
        Asset
      </button>
      <AssetPicker
        className="inbox-asset-picker"
        open={open}
        onOpenChange={setOpen}
        assets={visible}
        sidebar={sidebar}
        kind="any"
        acceptedMimeTypes={ACCEPTED_MIME_TYPES}
        search={search}
        onSearchChange={setSearch}
        onUpload={handleUpload}
        onSelect={(picked: Asset[]) => {
          picked.forEach((asset: Asset) => {
            onInsert(assetToHtml(asset))
          })
          setOpen(false)
        }}
        title="Insert asset"
        description={
          uploadFolder
            ? `Uploads will be added to “${uploadFolder}”.`
            : 'Upload a new file or pick one from your library.'
        }
        confirmLabel="Insert"
      />
    </>
  )
}

export default AssetInsertButton
